import {
  Avatar,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Theme,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Check from '@mui/icons-material/Check';
import React, { FC, useCallback, useState } from 'react';
import packageJSON from '../../../package.json';
import { useAppSelector } from '../../app/hooks';
import {
  selectFeatureFlagHitl3,
  selectIsHitl7Enabled,
  selectFeatureFlagTaskRouter,
  selectNodeEnv,
} from '../../redux/features/config/config.selector';
import {
  AgentEvents,
  AgentStatuses,
} from '../../redux/features/taskRouter/taskRouter.constants';
import { selectAgentStatus } from '../../redux/features/taskRouter/taskRouter.selector';
import { selectUserProfile } from '../../selectors/user';
import Colors from '../../utils/color';
import { removeTaskRouterInfoFromLS } from '../../utils/local-storage';
import ConfirmDialog from '../common/ConfirmDialog';
import { openKeyboardShortcutsHelp } from '../common/KeyboardShortcutsHelp';
import { useLayoutPreference } from './LayoutPreferenceContext';

interface ProfileMenuProps {
  logout?: Function;
  sendAgentEvent?: Function;
  handleAgentAvailability?: (agentStatus: AgentStatuses) => void;
  isTaskRouterPage?: boolean;
}

// Availability options surfaced inside the menu (hitl_3_0). `color` drives both
// the presence dot and the avatar background for that status; `contrast` is the
// avatar's initials colour on that background. Available reuses the amber used
// for the default profile avatar.
const AVAILABILITY_OPTIONS: {
  value: AgentStatuses;
  label: string;
  color: string;
  contrast: string;
}[] = [
  {
    value: AgentStatuses.available,
    label: 'Available',
    color: Colors.amber,
    contrast: Colors.primaryButtonTextColor,
  },
  {
    value: AgentStatuses.unavailable,
    label: 'Unavailable',
    color: Colors.guardsmanRed,
    contrast: Colors.white,
  },
  {
    value: AgentStatuses.training,
    label: 'In training',
    color: Colors.silverChalice,
    contrast: Colors.primaryButtonTextColor,
  },
];

/**
 * HITL-1667: the avatar and its button, trimmed under 7.0 so the header row
 * stops overflowing at 1024px.
 *
 * The avatar is the last thing in the row, so it is the first thing to be
 * pushed off the edge — and it is also the cheapest thing to shrink, being two
 * initials in a circle rather than anything anyone reads. 32 → 26 plus the
 * button's 4px insets → 2px returns 10px to the row.
 *
 * Inline `style` rather than new makeStyles rules, for two reasons: adding a
 * rule renumbers JSS class names across unrelated snapshot suites, and MUI's
 * own Avatar emotion styles outrank makeStyles for width/height anyway (the
 * same reason the colours below already go through `sx`).
 */
const HITL7_AVATAR_STYLE: React.CSSProperties = { width: 26, height: 26 };
const HITL7_AVATAR_BUTTON_STYLE: React.CSSProperties = { padding: 2 };

const useStyles = makeStyles((theme: Theme) => ({
  iconButton: {
    padding: theme.spacing(0.5),
    flexShrink: 0,
  },
  avatar: {
    // Only the size lives here. Colours AND typography go via the `sx` prop
    // below: MUI's own Avatar emotion styles override makeStyles for those, so
    // the initials would otherwise fall back to MUI's default Avatar font
    // instead of the app's typography.
    width: 32,
    height: 32,
  },
  menuHeader: {
    padding: theme.spacing(1, 2),
    maxWidth: 280,
  },
  name: {
    fontSize: '0.85rem',
    fontWeight: 600,
    wordBreak: 'break-word',
  },
  email: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    wordBreak: 'break-word',
  },
  versionLine: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    // Align with the menu items (same horizontal padding as MUI MenuItem).
    padding: theme.spacing(0.75, 2),
  },
  version: {
    // Same font + colour as the "Log out" menu item (MUI MenuItem renders
    // body1 = 1rem, text.primary, theme font).
    fontSize: '1rem',
    color: theme.palette.text.primary,
  },
  envChip: {
    height: 18,
    fontSize: '0.65rem',
    fontWeight: 700,
  },
  sectionLabel: {
    padding: theme.spacing(0.75, 2, 0.25),
    fontSize: '0.65rem',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
  },
  statusDot: {
    width: 9,
    height: 9,
    borderRadius: '50%',
    marginRight: theme.spacing(1.25),
    flexShrink: 0,
  },
  statusCheck: {
    marginLeft: 'auto',
    fontSize: '1.1rem',
    color: theme.palette.text.secondary,
  },
}));

/**
 * Header account control — replaces the old "Log out" button. Shows the agent's
 * two-letter initials in an avatar styled to match the restaurant dropdown
 * (theme secondary = amber bg + black text). The menu carries the agent
 * identity, the app version + build age (moved out of the toolbar), an env badge
 * off-production, the "Layout preference" entry ("Preferences" under 7.0), and
 * the existing logout flow (confirm dialog + session clear).
 */
const ProfileMenu: FC<ProfileMenuProps> = ({
  logout,
  sendAgentEvent,
  handleAgentAvailability,
  isTaskRouterPage,
}) => {
  const classes = useStyles();
  const userProfile = useAppSelector(selectUserProfile);
  const isHitl3 = useAppSelector(selectFeatureFlagHitl3);
  const isHitl7 = useAppSelector(selectIsHitl7Enabled);
  const isTaskRouterOn = useAppSelector(selectFeatureFlagTaskRouter);
  const agentStatus = useAppSelector(selectAgentStatus);
  const nodeEnv = useAppSelector(selectNodeEnv);

  // hitl_3_0 folds the task-router availability selector into this menu.
  const showAvailability = Boolean(isHitl3 && isTaskRouterPage);
  // When availability is surfaced, the avatar itself reflects the current
  // status (amber = available, red = unavailable, grey = in training).
  const activeStatus = AVAILABILITY_OPTIONS.find(
    (o) => o.value === agentStatus
  );
  const statusAvatar = showAvailability ? activeStatus : undefined;
  const { open: openLayoutPreference } = useLayoutPreference();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const isMenuOpen = Boolean(anchorEl);

  const firstName = userProfile?.firstName?.trim() ?? '';
  const lastName = userProfile?.lastName?.trim() ?? '';
  const fullName = `${firstName} ${lastName}`.trim();
  const email = userProfile?.email ?? '';

  const initials =
    firstName || lastName
      ? `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
      : (userProfile?.username || email).trim().charAt(0).toUpperCase() || '?';

  // Version line: app name + version, plus an env badge off production.
  const isProduction = (nodeEnv || '').toUpperCase() === 'PRODUCTION';

  const handleClose = () => setAnchorEl(null);

  const handleLogout = useCallback(() => {
    if (isTaskRouterOn) {
      sendAgentEvent?.(AgentEvents.logout);
      removeTaskRouterInfoFromLS();
    }
    logout?.();
  }, [isTaskRouterOn, sendAgentEvent, logout]);

  return (
    <>
      <IconButton
        className={classes.iconButton}
        style={isHitl7 ? HITL7_AVATAR_BUTTON_STYLE : undefined}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        aria-controls="profile-menu"
        aria-haspopup="true"
        aria-label="Account menu"
        size="small"
        data-testid="profile-menu-button"
      >
        <Avatar
          className={classes.avatar}
          style={isHitl7 ? HITL7_AVATAR_STYLE : undefined}
          sx={(theme) => ({
            // Status-driven when availability is surfaced, else the default
            // amber profile avatar.
            bgcolor: statusAvatar?.color ?? theme.palette.secondary.main,
            color:
              statusAvatar?.contrast ?? theme.palette.secondary.contrastText,
            // Exactly match the other header text: theme font ('Prompt'),
            // 0.75rem, regular weight (initials are already upper-cased in JS).
            fontFamily: theme.typography.fontFamily,
            fontSize: '0.75rem',
            fontWeight: theme.typography.fontWeightRegular,
          })}
        >
          {initials}
        </Avatar>
      </IconButton>
      <Menu
        id="profile-menu"
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        // The menu renders in a portal (outside the header), so mark it so the
        // AI auto-mode intercept listener ignores clicks on its items.
        data-profile-menu="true"
      >
        <div className={classes.menuHeader}>
          {fullName && (
            <Typography className={classes.name}>{fullName}</Typography>
          )}
          {email && <Typography className={classes.email}>{email}</Typography>}
        </div>
        <Divider />
        {/* Rendered as a flat array (not a Fragment): MUI's MenuList maps over
            its direct children for focus/keyboard handling and a Fragment would
            hide these items from it. */}
        {showAvailability && [
          <Typography key="availability-label" className={classes.sectionLabel}>
            Availability
          </Typography>,
          ...AVAILABILITY_OPTIONS.map(({ value, label, color }) => (
            <MenuItem
              key={value}
              selected={agentStatus === value}
              data-testid={`availability-option-${value}`}
              onClick={() => {
                handleClose();
                handleAgentAvailability?.(value);
              }}
            >
              <span
                className={classes.statusDot}
                style={{ backgroundColor: color }}
              />
              {label}
              {agentStatus === value && (
                <Check className={classes.statusCheck} fontSize="small" />
              )}
            </MenuItem>
          )),
          <Divider key="availability-divider" />,
        ]}
        <div className={classes.versionLine}>
          <Typography className={classes.version}>
            HITL - v{packageJSON.version}
          </Typography>
          {!isProduction && nodeEnv && (
            <Chip
              className={classes.envChip}
              label={nodeEnv}
              size="small"
              color="warning"
            />
          )}
        </div>
        {/* Always available in the authenticated app (lobby, landing, or an
            active restaurant), irrespective of the per-restaurant hitl_3_0 flag.
            The dialog is hosted by LayoutPreferenceProvider (App subtree); Apply
            always persists the split and only resizes live panes when the 3-pane
            console is actually mounted. */}
        <MenuItem
          onClick={() => {
            handleClose();
            openLayoutPreference();
          }}
        >
          {/* HITL-1695: 7.0 adds display choices, so it is no longer only layout. */}
          {isHitl7 ? 'Preferences' : 'Layout preference'}
        </MenuItem>
        {/* HITL-1702: the "?" button left the 7.0 header to free room for the
            indicators; this is its way in now (Alt+/ still toggles it). Only
            where HeaderIndicators — which hosts the dialog — is mounted, so not
            on the task-router lobby. */}
        {isHitl7 && !isTaskRouterPage && (
          <MenuItem
            data-testid="profile-menu-keyboard-shortcuts"
            onClick={() => {
              handleClose();
              openKeyboardShortcutsHelp();
            }}
          >
            Keyboard shortcuts (Alt+/)
          </MenuItem>
        )}
        <Divider />
        <MenuItem
          onClick={() => {
            handleClose();
            setShowLogoutConfirm(true);
          }}
        >
          Log out
        </MenuItem>
      </Menu>
      {showLogoutConfirm && (
        <ConfirmDialog
          content="Do you want to log out from HITL?"
          open={showLogoutConfirm}
          onConfirm={handleLogout}
          onClose={() => setShowLogoutConfirm(false)}
          // Renders in a portal (outside the header/menu), so mark it too — else
          // confirming logout in AI auto mode pops the "intercept order?" dialog.
          data-profile-menu="true"
        />
      )}
    </>
  );
};

export default ProfileMenu;
